/**
 * Single source of truth for the props every fixed-layout admin table needs.
 *
 * Spread this onto Mantine `<Table>` instead of wiring `style` / `styles` /
 * `classNames` by hand:
 *
 * ```tsx
 * <Table striped highlightOnHover withTableBorder stickyHeader {...getAdminTableProps(1300)}>
 * ```
 *
 * Bundling them keeps the three concerns that have to agree — fixed layout,
 * sticky-header painting, and cell containment — impossible to apply
 * partially. Cell containment in particular used to be a per-cell `truncate`
 * prop, which meant every new column started out bleeding into its neighbour
 * until someone spotted it. Enforced by `adminTablePropsCoverage.test.ts`.
 */

import type { CSSProperties } from 'react';
import { getAdminResizableTableStyle } from './getAdminResizableTableStyle';
import { getAdminStickyTableStyles } from './AdminStickyListLayout';
import classes from './adminTableCell.module.css';

export interface AdminTableProps {
  /** Fixed layout + minimum width so wide tables pan at page level. */
  style: CSSProperties;

  /** Sticky-header painting (opaque cells, separated borders). */
  styles: ReturnType<typeof getAdminStickyTableStyles>;

  /** Cell containment so overflowing content clips instead of bleeding. */
  classNames: { th: string; td: string };
}

/**
 * Returns the standard `<Table>` props for an admin list table.
 *
 * @param minWidthPx Width below which the table pans horizontally instead of
 * compressing columns past readability.
 */
export function getAdminTableProps(minWidthPx: number): AdminTableProps {
  return {
    style: getAdminResizableTableStyle(minWidthPx),
    styles: getAdminStickyTableStyles(),
    classNames: { th: classes.cell, td: classes.cell },
  };
}
